import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../services/api';

interface ApiConfig {
    id: string;
    service_name: string;
    provider: string;
    api_key: string;
    base_url: string;
    is_active: boolean;
}

const ApiControl: React.FC = () => {
    const [configs, setConfigs] = useState<ApiConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Partial<ApiConfig>>({
        service_name: 'hotel',
        provider: '',
        api_key: '',
        base_url: '',
        is_active: true
    });

    // Paystack Specific State
    const [paystackPk, setPaystackPk] = useState('');
    const [paystackSk, setPaystackSk] = useState('');
    const [isSavingPaystack, setIsSavingPaystack] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [showPaystackGuide, setShowPaystackGuide] = useState(true);

    const liveWebhookUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/bookings/paystack/webhook`;
    const liveCallbackUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/booking/confirmation`;

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        try {
            const data = await apiFetch('/admin/api-config');
            const confList: ApiConfig[] = data || [];
            setConfigs(confList);

            // Sync Paystack config
            const pConfig = confList.find(
                c => c.service_name === 'paystack' || (c.service_name === 'payment' && c.provider?.toLowerCase().includes('paystack'))
            );
            if (pConfig) {
                setPaystackSk(pConfig.api_key || '');
                setPaystackPk(pConfig.base_url || '');
            }
        } catch (error) {
            console.error('Error fetching configs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = (text: string, fieldName: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 2500);
    };

    const handleSavePaystack = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSavingPaystack(true);
        try {
            const existingPConfig = configs.find(
                c => c.service_name === 'paystack' || (c.service_name === 'payment' && c.provider?.toLowerCase().includes('paystack'))
            );

            if (existingPConfig) {
                await apiFetch(`/admin/api-config/${existingPConfig.id}`, {
                    method: 'PUT',
                    body: JSON.stringify({
                        service_name: 'payment',
                        provider: 'Paystack',
                        api_key: paystackSk.trim(),
                        base_url: paystackPk.trim(),
                        is_active: true
                    })
                });
            } else {
                await apiFetch('/admin/api-config', {
                    method: 'POST',
                    body: JSON.stringify({
                        service_name: 'payment',
                        provider: 'Paystack',
                        api_key: paystackSk.trim(),
                        base_url: paystackPk.trim(),
                        is_active: true
                    })
                });
            }

            alert('Live Paystack configuration saved successfully!');
            fetchConfigs();
        } catch (error: any) {
            console.error('Error saving Paystack config:', error);
            alert(`Error saving Paystack configuration: ${error.message || 'Server error'}`);
        } finally {
            setIsSavingPaystack(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (formData.id) {
                await apiFetch(`/admin/api-config/${formData.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(formData)
                });
            } else {
                await apiFetch('/admin/api-config', {
                    method: 'POST',
                    body: JSON.stringify(formData)
                });
            }

            setFormData({
                service_name: 'hotel',
                provider: '',
                api_key: '',
                base_url: '',
                is_active: true
            });
            setIsEditing(false);
            fetchConfigs();
        } catch (error) {
            console.error('Error saving config:', error);
            alert('Error saving configuration');
        }
    };

    const handleEdit = (config: ApiConfig) => {
        setFormData(config);
        setIsEditing(true);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this configuration?')) return;

        try {
            await apiFetch(`/admin/api-config/${id}`, {
                method: 'DELETE'
            });
            fetchConfigs();
        } catch (error) {
            console.error('Error deleting config:', error);
            alert('Error deleting configuration');
        }
    };

    if (loading) return <div className="text-white p-8">Loading configuration...</div>;

    const isPaystackLive = paystackPk.startsWith('pk_live_') && paystackSk.startsWith('sk_live_');
    const hasPaystackConfig = Boolean(paystackPk || paystackSk);

    return (
        <div className="space-y-8 p-6 md:p-8 font-sans">
            
            {/* Page Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">API & Payment Control</h1>
                    <p className="text-gray-400 mt-1 text-sm">
                        Manage live gateway keys, LetSFG credentials, and payment webhooks.
                    </p>
                </div>
                <button
                    onClick={() => {
                        setFormData({
                            service_name: 'hotel',
                            provider: '',
                            api_key: '',
                            base_url: '',
                            is_active: true
                        });
                        setIsEditing(!isEditing);
                    }}
                    className="px-5 py-2.5 bg-logo-gold-400 text-black rounded-xl hover:bg-logo-gold-500 transition-colors font-bold text-sm shadow-md cursor-pointer"
                >
                    {isEditing ? 'Cancel Custom Form' : '+ Add Custom API'}
                </button>
            </div>

            {/* ══════════════════════════════════════════════════════════ */}
            {/* Paystack Live Gateway Integration Card */}
            {/* ══════════════════════════════════════════════════════════ */}
            <div className="bg-gradient-to-br from-slate-900 via-gray-900 to-slate-950 rounded-3xl p-6 md:p-8 border border-logo-gold-500/25 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-logo-gold-500/10 rounded-full blur-3xl pointer-events-none"></div>

                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 pb-6 border-b border-gray-800 gap-4 relative z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center text-white shadow-lg shadow-emerald-500/20">
                            <i className="ri-secure-payment-line text-2xl"></i>
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h2 className="text-xl font-bold text-white tracking-tight">Paystack Gateway (Live Integration)</h2>
                                <span className={`px-3 py-0.5 rounded-full text-xs font-bold border ${isPaystackLive
                                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                    : hasPaystackConfig
                                        ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                                        : 'bg-gray-800 text-gray-400 border-gray-700'
                                    }`}>
                                    {isPaystackLive ? '● LIVE ACTIVE' : hasPaystackConfig ? '● TEST / PARTIAL' : 'NOT CONFIGURED'}
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                                Receive direct card payments from customers and trigger automatic LetSFG airline ticketing.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowPaystackGuide(!showPaystackGuide)}
                        className="text-xs font-semibold text-logo-gold-400 hover:text-logo-gold-300 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-logo-gold-500/10 border border-logo-gold-500/20 transition-all cursor-pointer"
                    >
                        <i className={`ri-information-line ${showPaystackGuide ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                        {showPaystackGuide ? 'Hide Setup Guide' : 'Show Setup Guide'}
                    </button>
                </div>

                {/* Step-by-Step Setup Guide Box */}
                {showPaystackGuide && (
                    <div className="mb-8 p-5 rounded-2xl bg-gray-950/70 border border-gray-800 text-sm text-gray-300 relative z-10 animate-fade-in">
                        <div className="flex items-center gap-2 mb-3 text-logo-gold-400 font-bold text-sm">
                            <i className="ri-compass-3-line text-lg"></i>
                            How to configure your live Paystack account with this project:
                        </div>
                        <ol className="list-decimal list-inside space-y-2 text-xs md:text-sm text-gray-300 leading-relaxed">
                            <li>
                                Log in to your <a href="https://dashboard.paystack.com" target="_blank" rel="noopener noreferrer" className="text-logo-gold-400 underline font-semibold hover:text-logo-gold-300">Paystack Dashboard</a>.
                            </li>
                            <li>
                                At the top navigation bar, toggle the switch from <strong>Test Mode</strong> to <strong>Live Mode</strong>.
                            </li>
                            <li>
                                In the left sidebar, click <strong>Settings</strong> &rarr; select the <strong>API Keys & Webhooks</strong> tab.
                            </li>
                            <li>
                                Copy your <strong>Live Public Key</strong> (starts with <code className="text-emerald-400 font-mono">pk_live_</code>) and paste it into the field below.
                            </li>
                            <li>
                                Copy your <strong>Live Secret Key</strong> (starts with <code className="text-emerald-400 font-mono">sk_live_</code>) and paste it into the field below.
                            </li>
                            <li>
                                Copy the <strong>Live Webhook URL</strong> below and paste it into Paystack's <strong>"Live Webhook URL"</strong> field.
                            </li>
                            <li>
                                Copy the <strong>Live Callback URL</strong> below and paste it into Paystack's <strong>"Live Callback URL"</strong> field.
                            </li>
                            <li>
                                Click <strong>Save Changes</strong> on Paystack and click <strong>"Save Live Keys"</strong> below.
                            </li>
                        </ol>
                    </div>
                )}

                {/* Live URLs Section (Copyable) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 relative z-10">
                    
                    {/* Live Webhook URL */}
                    <div className="bg-gray-950/80 rounded-2xl p-4 border border-gray-800 flex flex-col justify-between">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-bold text-gray-300 flex items-center gap-1.5 uppercase tracking-wider">
                                    <i className="ri-pulse-line text-logo-gold-400"></i>
                                    Live Webhook URL
                                </label>
                                <span className="text-[11px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded font-mono border border-emerald-500/20">
                                    POST /webhook
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 mb-3">
                                Paste this in Paystack &gt; Settings &gt; API Keys &amp; Webhooks &gt; <strong>Live Webhook URL</strong>.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                readOnly
                                value={liveWebhookUrl}
                                className="w-full bg-gray-900 border border-gray-700 text-gray-200 text-xs font-mono rounded-lg px-3 py-2 focus:outline-none select-all"
                            />
                            <button
                                type="button"
                                onClick={() => handleCopy(liveWebhookUrl, 'webhook')}
                                className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold transition-all flex items-center gap-1 whitespace-nowrap cursor-pointer border border-white/15"
                            >
                                <i className={copiedField === 'webhook' ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line'}></i>
                                {copiedField === 'webhook' ? 'Copied!' : 'Copy URL'}
                            </button>
                        </div>
                    </div>

                    {/* Live Callback URL */}
                    <div className="bg-gray-950/80 rounded-2xl p-4 border border-gray-800 flex flex-col justify-between">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-bold text-gray-300 flex items-center gap-1.5 uppercase tracking-wider">
                                    <i className="ri-arrow-go-back-line text-logo-gold-400"></i>
                                    Live Callback URL
                                </label>
                                <span className="text-[11px] text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded font-mono border border-blue-500/20">
                                    GET /confirmation
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 mb-3">
                                Paste this in Paystack &gt; Settings &gt; API Keys &amp; Webhooks &gt; <strong>Live Callback URL</strong>.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                readOnly
                                value={liveCallbackUrl}
                                className="w-full bg-gray-900 border border-gray-700 text-gray-200 text-xs font-mono rounded-lg px-3 py-2 focus:outline-none select-all"
                            />
                            <button
                                type="button"
                                onClick={() => handleCopy(liveCallbackUrl, 'callback')}
                                className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold transition-all flex items-center gap-1 whitespace-nowrap cursor-pointer border border-white/15"
                            >
                                <i className={copiedField === 'callback' ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line'}></i>
                                {copiedField === 'callback' ? 'Copied!' : 'Copy URL'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Keys Form */}
                <form onSubmit={handleSavePaystack} className="space-y-4 relative z-10 bg-gray-950/50 p-5 rounded-2xl border border-gray-800">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider mb-1.5">
                                Live Public Key (pk_live_...)
                            </label>
                            <input
                                type="text"
                                required
                                value={paystackPk}
                                onChange={(e) => setPaystackPk(e.target.value)}
                                placeholder="pk_live_xxxxxxxxxxxxxxxxxxxx"
                                className="w-full bg-gray-900 border border-gray-700 focus:border-logo-gold-400 text-white text-xs font-mono rounded-xl px-3.5 py-2.5 focus:outline-none"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">
                                Used by customer checkout to initialize secure live payment.
                            </p>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider mb-1.5">
                                Live Secret Key (sk_live_...)
                            </label>
                            <input
                                type="password"
                                required
                                value={paystackSk}
                                onChange={(e) => setPaystackSk(e.target.value)}
                                placeholder="sk_live_xxxxxxxxxxxxxxxxxxxx"
                                className="w-full bg-gray-900 border border-gray-700 focus:border-logo-gold-400 text-white text-xs font-mono rounded-xl px-3.5 py-2.5 focus:outline-none"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">
                                Used exclusively server-side to verify transactions and signature hashes.
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end pt-2">
                        <button
                            type="submit"
                            disabled={isSavingPaystack}
                            className="bg-logo-gold-400 hover:bg-logo-gold-500 text-black px-6 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-logo-gold-400/20 transition-all cursor-pointer flex items-center gap-2 disabled:opacity-50"
                        >
                            <i className="ri-save-line"></i>
                            <span>{isSavingPaystack ? 'Saving Keys...' : 'Save Live Paystack Keys'}</span>
                        </button>
                    </div>
                </form>
            </div>

            {/* Custom Configuration Form */}
            {isEditing && (
                <div className="bg-gray-900 shadow-xl rounded-2xl p-6 border border-gray-800 animate-fade-in">
                    <h3 className="text-lg font-bold text-white mb-4">
                        {formData.id ? 'Edit API Configuration' : 'New API Configuration'}
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-400">Service Name</label>
                                <select
                                    required
                                    className="mt-1 block w-full py-2.5 px-3 border border-gray-700 bg-gray-800 text-white rounded-xl shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm"
                                    value={formData.service_name}
                                    onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                                >
                                    <option value="hotel">Hotel (LetSFG)</option>
                                    <option value="flight">Flight (LetSFG)</option>
                                    <option value="payment">Payment (Paystack)</option>
                                    <option value="taxi">Taxi</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400">Provider</label>
                                {formData.service_name === 'payment' ? (
                                    <select
                                        required
                                        className="mt-1 block w-full py-2.5 px-3 border border-gray-700 bg-gray-800 text-white rounded-xl shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm"
                                        value={formData.provider}
                                        onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                                    >
                                        <option value="">Select Provider</option>
                                        <option value="Paystack">Paystack (Live)</option>
                                        <option value="Flutterwave">Flutterwave</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        required
                                        className="mt-1 block w-full py-2.5 px-3 border border-gray-700 bg-gray-800 text-white rounded-xl shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm"
                                        value={formData.provider}
                                        onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                                        placeholder="e.g. LetsFG, Amadeus"
                                    />
                                )}
                            </div>

                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-400">
                                    {formData.provider === 'Paystack' ? 'Paystack Secret Key (sk_live_... / sk_test_...)' : 'API Key / Token'}
                                </label>
                                <input
                                    type="password"
                                    className="mt-1 block w-full py-2.5 px-3 border border-gray-700 bg-gray-800 text-white rounded-xl shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm font-mono"
                                    value={formData.api_key}
                                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                                    placeholder={formData.provider === 'Paystack' ? 'sk_live_...' : 'Enter API Key'}
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-400">
                                    {formData.provider === 'Paystack' ? 'Paystack Public Key (pk_live_... / pk_test_...)' : 'Base URL / Endpoint'}
                                </label>
                                <input
                                    type="text"
                                    className="mt-1 block w-full py-2.5 px-3 border border-gray-700 bg-gray-800 text-white rounded-xl shadow-sm focus:outline-none focus:ring-logo-gold-400 focus:border-logo-gold-400 sm:text-sm font-mono"
                                    value={formData.base_url}
                                    onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                                    placeholder={formData.provider === 'Paystack' ? 'pk_live_...' : 'e.g. https://letsfg.co/developers/api/v1 (or /v1/sandbox)'}
                                />
                                <p className="mt-1 text-xs text-gray-400">
                                    {formData.provider === 'Paystack'
                                        ? 'For live Paystack payments, enter your Secret Key (starts with sk_live_) and Public Key (starts with pk_live_).'
                                        : 'For LetSFG Test API, use https://letsfg.co/developers/api/v1/sandbox. For Real API, use https://letsfg.co/developers/api/v1.'}
                                </p>
                            </div>

                            <div className="flex items-center">
                                <input
                                    id="is_active"
                                    type="checkbox"
                                    className="h-4 w-4 text-logo-gold-400 focus:ring-logo-gold-400 border-gray-700 rounded bg-gray-800"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-300 font-medium">
                                    Active &amp; Enabled
                                </label>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setIsEditing(false)}
                                className="py-2 px-4 rounded-xl text-sm font-medium text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="py-2 px-5 rounded-xl text-sm font-bold text-black bg-logo-gold-400 hover:bg-logo-gold-500 transition-colors shadow-md"
                            >
                                Save Configuration
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Active Configs Table */}
            <div className="bg-gray-900 shadow-xl rounded-2xl overflow-hidden border border-gray-800">
                <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-gray-950/40">
                    <h3 className="font-bold text-white text-base">All Registered Service Integrations</h3>
                    <span className="text-xs text-gray-400 font-mono">{configs.length} configured</span>
                </div>
                <table className="min-w-full divide-y divide-gray-800">
                    <thead className="bg-gray-800/60">
                        <tr>
                            <th scope="col" className="px-6 py-3.5 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                                Service
                            </th>
                            <th scope="col" className="px-6 py-3.5 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                                Provider
                            </th>
                            <th scope="col" className="px-6 py-3.5 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                                Status
                            </th>
                            <th scope="col" className="px-6 py-3.5 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-gray-900 divide-y divide-gray-800">
                        {configs.map((config) => (
                            <tr key={config.id} className="hover:bg-gray-800/40 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2.5 py-1 inline-flex text-xs leading-5 font-bold rounded-lg bg-gray-800 text-gray-200 border border-gray-700 capitalize">
                                        {config.service_name}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 font-medium">
                                    {config.provider}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2.5 py-1 inline-flex text-xs font-bold rounded-lg ${config.is_active
                                            ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                                            : 'bg-red-500/15 text-red-400 border border-red-500/30'
                                        }`}>
                                        {config.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                                    <button
                                        onClick={() => handleEdit(config)}
                                        className="text-logo-gold-400 hover:text-logo-gold-300 font-semibold transition-colors cursor-pointer"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(config.id)}
                                        className="text-red-400 hover:text-red-300 font-semibold transition-colors cursor-pointer"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {configs.length === 0 && !loading && (
                            <tr>
                                <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-400">
                                    No API configurations found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ApiControl;
